import { useCallback, useMemo } from 'react';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/components';
import {
  useGetAreaCodes,
  useGetCardTypes,
} from '@xhype/card-app-sdk';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateUserCardHolder } from '@package/smart-wallet/hooks';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/* Schemas */
export const RegisterOptionalDataFormSchema = z.object({
  cardTypeId: z.string().min(1, { message: 'Card Type is required' }),
  firstName: z.string().min(1, { message: 'First Name is required' }),
  lastName: z.string().min(1, { message: 'Last Name is required' }),
  areaCode: z.string().min(1, { message: 'Area Code is required' }),
  mobile: z
    .string()
    .min(1, { message: 'Mobile number is required' })
    .refine((value) => /^\d+$/.test(value), {
      message: 'Invalid mobile number',
    }),
});

/* Types */
export type RegisterOptionalDataFormType = z.infer<
  typeof RegisterOptionalDataFormSchema
>;

/* Internal components */
const CreateCardHolderLiteForm = () => {
  const { register } = useCreateUserCardHolder({});
  const form = useForm<RegisterOptionalDataFormType>({
    resolver: zodResolver(RegisterOptionalDataFormSchema),
  });

  /* Queries */
  const { data: areaCodes } = useGetAreaCodes({
    onError: (error: Error) => {
      console.error(`Get Area Codes: Axios error occurred - ${error.message}`);
    },
  });

  const { data: cardTypes } = useGetCardTypes({
    onError: (error: Error) => {
      console.error(`Get Card Type: Axios error occurred - ${error.message}`);
    },
  });

  /* Callbacks */
  const onSubmit = useCallback(
    async (data: RegisterOptionalDataFormType) => {
      try {
        await register(data);
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form],
  );

  /* Memos */
  const mobileAreaCodes = useMemo(
    () => areaCodes?.map((areaCode) => areaCode.code) || [],
    [areaCodes],
  );

  const cardTypesId = useMemo(
    () => cardTypes?.map((cardType) => cardType.cardTypeId.toString()) || [],
    [cardTypes],
  );

  const formFields = useMemo(() => {
    return [
      {
        name: 'cardTypeId',
        label: 'Card Type',
        type: 'dropdown',
        options: cardTypesId,
      },
      { name: 'firstName', label: 'First Name', type: 'input' },
      { name: 'lastName', label: 'Last Name', type: 'input' },
      {
        name: 'areaCode',
        label: 'Area Code',
        type: 'dropdown',
        options: mobileAreaCodes,
      },
      { name: 'mobile', label: 'Mobile', type: 'input' },
    ];
  }, [cardTypesId, mobileAreaCodes]);

  /* Render */
  return (
    <Form {...form}>
      <form
        className='w-full flex flex-col gap-2'
        onSubmit={form.handleSubmit(onSubmit)}
      >
        {formFields.map(({ name, label, type, options }) => (
          <FormField
            key={name}
            control={form.control}
            name={name as keyof RegisterOptionalDataFormType}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  {type === 'dropdown' ? (
                    <select {...field} className='border rounded p-2'>
                      <option value=''>Select {label}</option>
                      {options?.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input {...field} placeholder={label} />
                  )}
                </FormControl>
                <FormMessage className='text-start' />
              </FormItem>
            )}
          />
        ))}
        <Button
          className='w-full cursor-pointer uppercase'
          type='submit'
          disabled={form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? 'Registering...' : 'Register'}
        </Button>
      </form>
    </Form>
  );
};

/* Main component */
export const CreateCardHolderLite = () => {
  return (
    <div className='h-full w-full flex flex-col items-center justify-center'>
      <section className='w-2/3 flex flex-col items-center justify-center gap-4'>
        <CreateCardHolderLiteForm />
      </section>
    </div>
  );
};
